import { createContext, useContext } from 'react';

import { hasFieldId } from './hiddenFieldIds';

interface ConditionalFieldsContextValue {
  hiddenFields: Set<string>;
  hiddenSections: Set<string>;
}

export const ConditionalFieldsContext =
  createContext<ConditionalFieldsContextValue>({
    hiddenFields: new Set(),
    hiddenSections: new Set(),
  });

export const useConditionalFieldsContext = () => {
  const context = useContext(ConditionalFieldsContext);
  if (!context) {
    throw new Error(
      'useConditionalFieldsContext must be used within a ConditionalFieldsProvider'
    );
  }

  return context;
};

/**
 * The hidden set is keyed by the persisted `FieldId`, which may be cased
 * differently from the canonical form-value id the caller holds — see
 * {@link hasFieldId}. Matching case-sensitively here silently rendered a field
 * whose condition had evaluated false (RSP-6818).
 */
export const useIsConditionalFieldVisible = (fieldId: string) => {
  const context = useConditionalFieldsContext();

  return !hasFieldId(context.hiddenFields, fieldId);
};

export const useIsConditionalSectionVisible = (sectionId: string) => {
  const context = useConditionalFieldsContext();

  return !context.hiddenSections.has(sectionId);
};
